import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import DataSource from 'devextreme/data/data_source';
import PivotGridDataSource from 'devextreme/ui/pivot_grid/data_source';
import ArrayStore from 'devextreme/data/array_store';
import {Workbook, Worksheet} from 'exceljs';
import {romanize, Domain} from 'shared';
import {saveAs} from 'file-saver';
import {UserDomain} from '../../../../user-domains/user-domain';
import {UserDomainService} from '../../../../user-domains/user-domain.service';
import {FieldsInterventionsPageRequest} from '../../../fields-interventions-page/fields-interventions-page-request';


export type ExportSubReport = (workBook: Workbook) => Promise<any>;
export type ExportSubReportElement =
  (workBook: Workbook, workSheet: Worksheet, topLeftCell: { row: number, column: number }) => Promise<any>;

export type LoadSubReport = (isReload: boolean, filter?: SubReportFilter, data?: any) => Promise<any>;

export interface SubReportInitialized {
  index: number;
  export: ExportSubReport;
  loader: LoadSubReport;
}

export class SubReportFilter {
  domains: Domain[] = [];

  domainIds(): number[] {
    return this.domains.map(d => d.id);
  }

  domainNames(): string[] {
    return this.domains.map(d => d.name);
  }
}

export interface SubReportElementInitialized {
  index: number;
  export: ExportSubReportElement;
  loader: LoadSubReport;
}

@Component({
  selector: 'app-fields-interventions-general-ii-report',
  templateUrl: './fields-interventions-general-ii-report.component.html',
  styleUrls: ['./fields-interventions-general-ii-report.component.scss']
})
export class FieldsInterventionsGeneralIIReportComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input()
  toolbarDs: DataSource;

  @Input()
  store: ArrayStore;

  @Input()
  dataSource: PivotGridDataSource;

  @Input()
  currentReportType: any;

  @Input()
  reportCurrentViewModes: any;

  @Input()
  width: number;

  @Input()
  height: number;

  @Input()
  request: FieldsInterventionsPageRequest;

  subReports: SubReportInitialized[] = [];

  index = 0;
  isLoading: boolean;

  domains: UserDomain[];
  isFilterVisible: boolean;

  filter = new SubReportFilter();

  constructor(
    private userDomainsService: UserDomainService,
  ) {

    // User domains
    this.domains = userDomainsService.domains;
    this.filter.domains = this.domains;
  }

  ngOnInit(): void {
    this.isLoading = true;
  }

  ngAfterViewInit(): void {

    this.toolbarDs.store().update('filter', {
      options: {
        onClick: () => {
          this.isFilterVisible = true;
        }
      }
    });
    this.toolbarDs.store().update('refresh', {
      options: {
        onClick: () => {
          this.isLoading = true;
          this.load(true).then(() => {
            this.isLoading = false;
          });
        }
      }
    });
    this.toolbarDs.store().insert({
      name: 'export',
      location: 'after',
      locateInMenu: 'never',
      widget: 'dxButton',
      options: {
        icon: 'export',
        text: 'export',
        onClick: () => {
          const workbook = new Workbook();
          const promises = [];
          this.subReports.sort((a, b) => a.index - b.index);
          this.subReports.forEach((e) => {
            promises.push(e.export(workbook));
          });
          Promise.all(promises).then(() => {
            workbook.xlsx.writeBuffer()
              .then((buffer: BlobPart) => {
                saveAs(new Blob([buffer], {type: 'application/octet-stream'}), 'DataGrid.xlsx');
              });
          });
        }
      }
    });
    this.toolbarDs.store().update('view', {visible: false});
    this.toolbarDs.reload();


    this.load(false).then(() => {
      this.isLoading = false;

      this.dataSource.on('loadingChanged', (isLoading) => {
        if (!isLoading) {
          this.isLoading = true;
          this.load(true).then(() => {
            this.isLoading = false;
          });
        }
      });
    });

  }

  load(isReload: boolean): Promise<any> {
    const promises = [];
    this.subReports.forEach((r) => {
      promises.push(r.loader(isReload, this.filter));
    });
    return Promise.all(promises);
  }

  subReportInitialized(e: SubReportInitialized) {
    this.subReports.push(e);
  }

  ngOnDestroy(): void {
    this.toolbarDs.store().update('view', {visible: true});
    this.toolbarDs.store().remove('export');
    this.toolbarDs.reload();
    this.dataSource.off('loadingChanged');
  }

  applyFilter = (e) => {
    this.isLoading = true;
    this.load(true).then(() => {
      this.isLoading = false;
      this.isFilterVisible = false;
    });
  }

  close = () => {
    this.isFilterVisible = false;
  }

  filterDomains(): string {
    if (this.filter.domains.length === this.domains.length) {
      return 'Tous';
    }
    return this.filter.domains.map(d => d.name).join(', ');
  }
}

@Component({
  template: ''
})
export abstract class SubReportComponent implements OnInit {

  public static inputs: string[] = ['index', 'store'];
  public static outputs = ['initialized'];

  @Input()
  index: number;

  @Input()
  store: ArrayStore;

  @Output()
  initialized: EventEmitter<SubReportInitialized> = new EventEmitter();

  romainIndex: string;

  subReports: SubReportElementInitialized[] = [];

  title: string;

  ngOnInit(): void {
    this.romainIndex = romanize(this.index);
  }

  async export(workbook: Workbook): Promise<any> {
    const worksheet = workbook.addWorksheet(this.getWorkSheetName());
    this.subReports.sort((a, b) => a.index - b.index);
    for (const exp of this.subReports) {
      await exp.export(workbook, worksheet, {row: worksheet.rowCount + 2, column: 1});
    }
  }

  load(isReload: boolean, filter: SubReportFilter, data: any): Promise<any> {
    const promises = [];
    this.subReports.forEach((r) => {
      promises.push(r.loader(isReload, filter, data));
    });
    return Promise.all(promises);
  }

  subReportElementInitialized(e: SubReportElementInitialized) {
    this.subReports.push(e);
  }

  abstract getWorkSheetName(): string;
}

@Component({
  template: ''
})
export class SubReportElementComponent implements OnInit {

  public static inputs: string[] = ['index', 'store'];
  public static outputs = ['initialized'];

  @Input()
  index: number;

  @Input()
  store: ArrayStore;

  @Output()
  initialized: EventEmitter<SubReportElementInitialized> = new EventEmitter();

  ngOnInit(): void {
  }
}
